<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'

import { useActiveCity } from '~/composables/useActiveCity'
import { useLeadNotice } from '~/composables/useLeadNotice'

const SUCCESS_HIDE_MS = 8000

const { notice, clear } = useLeadNotice()
const activeCity = useActiveCity()

const phone = computed<string>(() => activeCity.value?.phone_number?.trim() ?? '')
const phoneHref = computed<string>(() => `tel:+${phone.value.replace(/\D/g, '')}`)

let hideTimer: ReturnType<typeof setTimeout> | undefined

watch(
  () => notice.value?.id,
  () => {
    clearTimeout(hideTimer)
    if (notice.value?.type === 'success') {
      hideTimer = setTimeout(clear, SUCCESS_HIDE_MS)
    }
  }
)

onBeforeUnmount(() => clearTimeout(hideTimer))
</script>

<template>
  <Transition name="lead-notice">
    <div
      v-if="notice"
      :key="notice.id"
      :role="notice.type === 'error' ? 'alert' : 'status'"
      class="bg-brand-white text-brand-dark fixed inset-x-4 bottom-4 z-300 mx-auto max-w-md rounded-2xl p-5 pr-12 text-base shadow-xl">
      <button
        type="button"
        aria-label="Закрыть"
        class="text-brand-grey hover:text-brand-red absolute top-3 right-4 text-2xl leading-none"
        @click="clear">
        ×
      </button>

      <template v-if="notice.type === 'success'">
        <div class="font-bold">Спасибо! Заявка принята.</div>
        <div class="mt-1 text-sm">Мы перезвоним в ближайшее время.</div>
      </template>

      <template v-else>
        <div class="text-brand-red font-bold">Не удалось отправить заявку.</div>
        <div class="mt-1 text-sm">
          <template v-if="phone">
            Позвоните нам:
            <a :href="phoneHref" class="text-brand-red font-semibold whitespace-nowrap">
              {{ phone }}
            </a>
            — или попробуйте ещё раз через минуту.
          </template>
          <template v-else>Попробуйте ещё раз через минуту.</template>
        </div>
      </template>
    </div>
  </Transition>
</template>

<style scoped>
.lead-notice-enter-active,
.lead-notice-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.lead-notice-enter-from,
.lead-notice-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
